// Benjamini-Hochberg FDR correction. Testing many models against the same
// symbol (or many symbols) means some will clear p < 0.05 purely by chance --
// this adjusts each group's p-values so "significant" accounts for how many
// hypotheses were tested alongside it, without discarding the original
// (unadjusted) p-value, which stays meaningful on its own.
export const applyBenjaminiHochberg = (
  rows,
  { groupBy, getPValue, alpha = 0.05 },
) => {
  const groups = new Map();
  rows.forEach((row) => {
    const key = groupBy(row);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  });

  const adjustedByRow = new Map();

  groups.forEach((groupRows) => {
    const withPValues = groupRows
      .map((row) => ({ row, pValue: getPValue(row) }))
      .filter(({ pValue }) => typeof pValue === "number" && !isNaN(pValue));

    const m = withPValues.length;
    if (!m) return;

    const byPValueAsc = [...withPValues].sort((a, b) => a.pValue - b.pValue);

    // adjusted_(i) = min_{k>=i}(p_(k) * m / k) -- walk ranks from m down to 1
    // so each adjusted value is never smaller than a higher-ranked one.
    let runningMin = 1;
    for (let rank = m; rank >= 1; rank--) {
      const { row, pValue } = byPValueAsc[rank - 1];
      runningMin = Math.min(runningMin, (pValue * m) / rank);
      adjustedByRow.set(row, runningMin);
    }
  });

  return rows.map((row) => {
    const pValueAdjusted = adjustedByRow.get(row);
    return {
      ...row,
      pValueAdjusted: pValueAdjusted === undefined ? null : pValueAdjusted,
      isSignificantAdjusted:
        pValueAdjusted === undefined ? null : pValueAdjusted < alpha,
    };
  });
};
